import { Component, Input, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  Car,
  Building2,
  Target,
  Utensils,
  ChevronRight,
  Calendar,
  Users,
  Leaf,
} from 'lucide-angular';
import { ChartDoughnutComponent } from '../chart-doughnut/chart-doughnut.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TooltipModule } from 'primeng/tooltip';
import { translateElementType } from '../../utils/element-type-translation';
import { ColorsService } from '../../services/colors.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { ChartSizeService } from '../../services/chart-size.service';
import { ImpactService } from '../../services/impact.service';
import { TooltipIconComponent } from 'src/app/shared/tooltip-icon/tooltip-icon.component';

interface ChartData {
  label: string;
  value: number;
  color: string;
  title?: string;
  isEcoFriendly?: boolean;
}

interface LegendItem {
  label: string;
  title?: string;
  impact: number;
  percentage: number;
  color: string;
  type: string;
  isEcoFriendly?: boolean;
}

@Component({
  selector: 'app-collapsible-section',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ChartDoughnutComponent,
    TranslocoModule,
    TooltipModule,
    TooltipIconComponent,
  ],
  templateUrl: './collapsible-section.component.html',
  styles: [
    `
      /* Simple doughnut chart placeholder styles */
      :host {
        display: block;
      }
    `,
  ],
})
export class CollapsibleSectionComponent {
  @Input() title!: string;
  @Input() icon!: string; // Deprecated, will use categoryType instead
  @Input() categoryType!:
    | 'transport'
    | 'accommodation'
    | 'activities'
    | 'food'
    | 'daily'
    | 'per-traveler';
  @Input() isOpen: boolean = false;
  @Input() chartData: ChartData[] = [];
  // @Input() topElements: TopElement[] = [];
  @Input() showPercentage: boolean = false;
  @Input() hasTimData?: boolean = false;

  @ViewChild(ChartDoughnutComponent) chartComponent?: ChartDoughnutComponent;

  // Icons
  carIcon = Car;
  buildingIcon = Building2;
  targetIcon = Target;
  utensilsIcon = Utensils;
  chevronRightIcon = ChevronRight;
  calendarIcon = Calendar;
  usersIcon = Users;
  leafIcon = Leaf;

  readonly Math = Math;
  private transloco = inject(TranslocoService);
  private colorsService = inject(ColorsService);
  formatCo2e = inject(FormatCo2eService);
  chartSize = inject(ChartSizeService);
  private impactService = inject(ImpactService);

  toggle() {
    this.isOpen = !this.isOpen;
  }

  hasData(): boolean {
    // For special sections without chart (daily), return false
    if (this.categoryType === 'daily') return false;

    // Show chart only if there's at least one data point (even with zero impact)
    return this.chartData.length > 0;
  }

  getTotalValue(): number {
    return this.chartData.reduce((sum, item) => sum + item.value, 0);
  }

  getTotalPercentage(): number {
    // Pour l'affichage en pourcentage, on considère que chaque section représente 100%
    return 100;
  }

  getIcon() {
    switch (this.categoryType) {
      case 'transport':
        return this.carIcon;
      case 'accommodation':
        return this.buildingIcon;
      case 'activities':
        return this.targetIcon;
      case 'food':
        return this.utensilsIcon;
      case 'daily':
        return this.calendarIcon;
      case 'per-traveler':
        return this.usersIcon;
      default:
        return this.carIcon;
    }
  }

  getCenterValue(): string {
    if (this.showPercentage) {
      return '100%';
    }
    return this.formatCo2e.formatValue(this.getTotalValue());
  }

  getCenterLabel(): string {
    if (this.showPercentage) {
      return 'Total';
    }
    return this.formatCo2e.getUnit(this.getTotalValue(), {
      perPerson: this.impactService.perPerson(),
    });
  }

  formatElementType(type: string): string {
    return translateElementType(this.transloco, type);
  }

  // Obtient les données du graphique triées par impact décroissant avec couleurs dégradées
  getSortedChartData(): ChartData[] {
    if (!this.chartData || this.chartData.length === 0) return [];

    // Trier par impact décroissant
    const sortedData = [...this.chartData].sort((a, b) => b.value - a.value);

    // Générer les couleurs dégradées
    const categoryType = this.getCategoryTypeForColors();
    if (categoryType) {
      const colors = this.colorsService.generateCategoryGradient(
        categoryType,
        sortedData.length,
      );
      return sortedData.map((item, index) => ({
        ...item,
        color: colors[index] || item.color,
      }));
    }

    return sortedData;
  }

  // Génère les données pour la légende enrichie
  getEnrichedLegendData(): LegendItem[] {
    const sortedData = this.getSortedChartData();
    const total = this.getTotalValue();

    return sortedData.map((item) => ({
      label: item.label,
      title: item.title,
      impact: item.value,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
      color: item.color,
      type: item.label,
      isEcoFriendly: item.isEcoFriendly,
    }));
  }

  // Détermine le type de catégorie pour les couleurs
  private getCategoryTypeForColors():
    | 'transport'
    | 'accommodation'
    | 'activity'
    | 'food'
    | 'waste'
    | null {
    switch (this.categoryType) {
      case 'transport':
        return 'transport';
      case 'accommodation':
        return 'accommodation';
      case 'activities':
        return 'activity';
      case 'food':
        return 'food';
      case 'daily':
        return null; // Pas de dégradé pour daily
      case 'per-traveler':
        return null;
      default:
        return null;
    }
  }

  formatImpactValue(impact: number): string {
    return this.formatCo2e.formatValue(impact);
  }

  getUnitForImpact(impact: number): string {
    return this.formatCo2e.getUnit(impact, {
      perPerson: this.impactService.perPerson(),
    });
  }

  // Methods to handle legend hover interactions
  onLegendItemHover(item: LegendItem, indexInList: number): void {
    if (!this.chartComponent) return;

    // In collapsible-section, there's only one category
    // so the index in the list = index in chartData (if same sort order)
    const chartData = this.getSortedChartData();

    if (indexInList >= 0 && indexInList < chartData.length) {
      this.chartComponent.highlightSegment(indexInList);
    }
    // Silently ignore out of bounds indices
  }

  onLegendItemLeave(): void {
    if (this.chartComponent) {
      this.chartComponent.clearHighlight();
    }
  }
}
